import * as React from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import { TONE_DOT_CLASSES, type Tone } from '@/components/data/tones';

export interface BoardGroup {
  key: string;
  label: string;
  tone?: Tone;
  /** Optional description shown as a tooltip on the column header. */
  description?: string;
}

export interface BoardProps<TItem> {
  items: TItem[];
  groups: BoardGroup[];
  /** Which column an item belongs to (e.g. `(p) => p.stage`). Items with an unknown group are skipped. */
  groupKey: (item: TItem) => string;
  getItemId: (item: TItem) => string;
  renderCard: (item: TItem, state: { isDragging: boolean }) => React.ReactNode;
  /** Called after a drop into another column. Persist it and refetch; the board is controlled by `items`. */
  onMove?: (itemId: string, fromGroup: string, toGroup: string) => void | Promise<void>;
  onCardClick?: (item: TItem) => void;
  /** Disable dragging (viewer role, loading). */
  readOnly?: boolean;
  /** Hide columns that have no items. */
  hideEmptyGroups?: boolean;
  className?: string;
  /** Label for the whole board (aria). */
  label?: string;
}

const COLUMN_PREFIX = 'column:';

function columnId(group: string): UniqueIdentifier {
  return `${COLUMN_PREFIX}${group}`;
}

/**
 * Kanban board on dnd-kit. Columns are droppables, cards are sortables; moving a card to another
 * column fires `onMove(itemId, from, to)`. Keyboard: Tab to a card, Space to lift, arrow keys to
 * move between columns, Space to drop, Esc to cancel.
 */
export function Board<TItem>({
  items,
  groups,
  groupKey,
  getItemId,
  renderCard,
  onMove,
  onCardClick,
  readOnly = false,
  hideEmptyGroups = false,
  className,
  label = 'Board',
}: BoardProps<TItem>) {
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const byGroup = React.useMemo(() => {
    const map = new Map<string, TItem[]>();
    for (const g of groups) map.set(g.key, []);
    for (const item of items) {
      const g = groupKey(item);
      map.get(g)?.push(item);
    }
    return map;
  }, [items, groups, groupKey]);

  const itemById = React.useMemo(() => {
    const map = new Map<string, TItem>();
    for (const item of items) map.set(getItemId(item), item);
    return map;
  }, [items, getItemId]);

  const groupOfId = (id: UniqueIdentifier): string | null => {
    const s = String(id);
    if (s.startsWith(COLUMN_PREFIX)) return s.slice(COLUMN_PREFIX.length);
    const item = itemById.get(s);
    return item ? groupKey(item) : null;
  };

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const from = groupOfId(active.id);
    const to = groupOfId(over.id);
    if (!from || !to || from === to) return;
    void onMove?.(String(active.id), from, to);
  };

  const activeItem = activeId ? itemById.get(activeId) : undefined;
  const visibleGroups = hideEmptyGroups
    ? groups.filter((g) => (byGroup.get(g.key)?.length ?? 0) > 0)
    : groups;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => `Picked up card ${active.id}.`,
          onDragOver: ({ over }) =>
            over ? `Over ${groupOfId(over.id) ?? over.id}.` : 'No longer over a column.',
          onDragEnd: ({ active, over }) =>
            over
              ? `Moved card ${active.id} to ${groupOfId(over.id) ?? over.id}.`
              : `Card ${active.id} was dropped.`,
          onDragCancel: ({ active }) => `Moving card ${active.id} was cancelled.`,
        },
      }}
    >
      <div
        role="region"
        aria-label={label}
        className={cn('scrollbar-thin flex gap-3 overflow-x-auto pb-2', className)}
      >
        {visibleGroups.map((group) => (
          <BoardColumn
            key={group.key}
            group={group}
            items={byGroup.get(group.key) ?? []}
            getItemId={getItemId}
            renderCard={renderCard}
            onCardClick={onCardClick}
            readOnly={readOnly}
          />
        ))}
      </div>
      <DragOverlay>
        {activeItem ? (
          <div className="rounded-md border bg-card p-3 shadow-lg ring-2 ring-primary/40">
            {renderCard(activeItem, { isDragging: true })}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

interface BoardColumnProps<TItem> {
  group: BoardGroup;
  items: TItem[];
  getItemId: (item: TItem) => string;
  renderCard: BoardProps<TItem>['renderCard'];
  onCardClick?: (item: TItem) => void;
  readOnly: boolean;
}

function BoardColumn<TItem>({
  group,
  items,
  getItemId,
  renderCard,
  onCardClick,
  readOnly,
}: BoardColumnProps<TItem>) {
  const { setNodeRef, isOver } = useDroppable({ id: columnId(group.key), disabled: readOnly });
  const ids = items.map(getItemId);
  const headingId = `board-col-${group.key}`;

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'flex w-72 shrink-0 flex-col rounded-lg border bg-muted/40 transition-colors',
        isOver && 'border-primary/60 bg-accent/60',
      )}
    >
      <header className="flex items-center gap-2 px-3 py-2">
        <span
          className={cn('h-2 w-2 rounded-full', TONE_DOT_CLASSES[group.tone ?? 'neutral'])}
          aria-hidden="true"
        />
        <h3 id={headingId} className="text-sm font-medium" title={group.description}>
          {group.label}
        </h3>
        <span className="ml-auto rounded-full bg-background px-2 text-xs tabular-nums text-muted-foreground">
          {items.length}
        </span>
      </header>
      <SortableContext id={String(columnId(group.key))} items={ids} strategy={verticalListSortingStrategy}>
        <ul
          ref={setNodeRef}
          className="flex min-h-[6rem] flex-1 flex-col gap-2 px-2 pb-2"
          aria-label={`${group.label} cards`}
        >
          {items.map((item) => (
            <BoardCard
              key={getItemId(item)}
              id={getItemId(item)}
              readOnly={readOnly}
              onClick={onCardClick ? () => onCardClick(item) : undefined}
            >
              {(state) => renderCard(item, state)}
            </BoardCard>
          ))}
        </ul>
      </SortableContext>
    </section>
  );
}

interface BoardCardProps {
  id: string;
  readOnly: boolean;
  onClick?: () => void;
  children: (state: { isDragging: boolean }) => React.ReactNode;
}

function BoardCard({ id, readOnly, onClick, children }: BoardCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: readOnly,
  });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };
  // The <li> keeps its list semantics; the inner element carries the dnd-kit role/tabindex/listeners.
  return (
    <li style={style} className={cn(isDragging && 'opacity-40')} data-card-id={id}>
      <div
        ref={setNodeRef}
        className={cn(
          'rounded-md border bg-card p-3 text-sm shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring',
          !readOnly && 'cursor-grab active:cursor-grabbing',
        )}
        onClick={onClick}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' && e.target === e.currentTarget) {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        {...attributes}
        {...listeners}
      >
        {children({ isDragging })}
      </div>
    </li>
  );
}
